export type PhonebookOption = {
  label: string;
  value: string;
  serviceGuid?: string | null;
  matchKey: string;
};

export const getPhonebookMatchKey = (item: any): string => {
  if (typeof item === 'string') {
    return item.trim().toLowerCase();
  }

  const key =
    item?.matchKey ??
    item?.teamGuid ??
    item?.name ??
    item?.phonebook ??
    item?.serviceTeamName ??
    item?.teamName ??
    item?.serviceGuid ??
    item?.guid ??
    item?.ocid ??
    item?.id ??
    item?.displayName ??
    item?.phonebookName ??
    item?.label ??
    item?.value ??
    '';

  return String(key).trim().toLowerCase();
};

export const buildManagerHierarchyPhonebookOptions = (phonebooks: any[] = []): PhonebookOption[] =>
  (Array.isArray(phonebooks) ? phonebooks : [])
    .map((item: any) => {
      if (typeof item === 'string') {
        return { label: item, value: item, matchKey: getPhonebookMatchKey(item) };
      }

      const label = item?.teamGuid ?? item?.displayName ?? item?.name ?? item?.phonebookName ?? item?.phonebook ?? item?.id;
      const value = item?.teamGuid ?? item?.phonebook ?? item?.id ?? item?.serviceGuid ?? label;
      const serviceGuid = item?.serviceGuid ?? item?.guid ?? item?.ocid ?? null;
      const matchKey = getPhonebookMatchKey(item);

      if (!label || !value) {
        return null;
      }

      return { label: String(label), value: String(value), serviceGuid, matchKey };
    })
    .filter((item: PhonebookOption | null): item is PhonebookOption => !!item)
    .filter(
      (item: PhonebookOption, index: number, arr: PhonebookOption[]) =>
        index === arr.findIndex((candidate: PhonebookOption) => candidate.matchKey === item.matchKey)
    );

export const buildDefaultPhonebookOptions = (
  phonebooks: any[] = [],
  shouldUseCorpSsoMetadataFlow: boolean,
  viewMoreLabel: string
): PhonebookOption[] => {
  if (!shouldUseCorpSsoMetadataFlow) {
    return [];
  }

  return [
    ...buildManagerHierarchyPhonebookOptions(phonebooks),
    { label: viewMoreLabel, value: '__VIEW_MORE__', matchKey: '__VIEW_MORE__' },
  ];
};

export const extractPhonebookItems = (parsed: any): any[] =>
  Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.items)
      ? parsed.items
      : Array.isArray(parsed?.data)
        ? parsed.data
        : Array.isArray(parsed?.results)
          ? parsed.results
          : Array.isArray(parsed?.phonebookServiceTeamList)
            ? parsed.phonebookServiceTeamList
            : Array.isArray(parsed?.phonebookServiceTeams)
              ? parsed.phonebookServiceTeams
              : [];

export const buildDynamicPhonebookOptions = (parsed: any): PhonebookOption[] =>
  extractPhonebookItems(parsed)
    .map((item: any) => {
      if (typeof item === 'string') {
        return { label: item, value: item, matchKey: getPhonebookMatchKey(item) };
      }

      const label =
        item?.name ??
        item?.displayName ??
        item?.phonebook ??
        item?.phonebookName ??
        item?.serviceTeamName ??
        item?.teamName ??
        item?.id ??
        item?.serviceGuid;
      const value =
        item?.teamGuid ??
        item?.phonebook ??
        item?.name ??
        item?.serviceGuid ??
        item?.id ??
        item?.guid ??
        item?.ocid ??
        label;
      const serviceGuid = item?.serviceGuid ?? item?.guid ?? item?.ocid ?? null;
      const matchKey = getPhonebookMatchKey(item);

      if (!label || !value) {
        return null;
      }

      return { label: String(label), value: String(value), serviceGuid, matchKey };
    })
    .filter((item: PhonebookOption | null): item is PhonebookOption => !!item);

export const mergePhonebookOptions = (
  defaultPhonebookOptions: PhonebookOption[],
  parsed: any
): { options: PhonebookOption[]; dynamicValues: Set<string> } => {
  const dynamicPhonebooks = buildDynamicPhonebookOptions(parsed);
  const staticPhonebooks = defaultPhonebookOptions.filter((item: PhonebookOption) => item?.value !== '__VIEW_MORE__');
  const managerHierarchyValues = new Set(staticPhonebooks.map((item: PhonebookOption) => getPhonebookMatchKey(item)));
  const additionalPhonebooks = dynamicPhonebooks.filter(
    (item: PhonebookOption) => !managerHierarchyValues.has(getPhonebookMatchKey(item))
  );
  const filteredPhonebooks = additionalPhonebooks.filter(
    (item: PhonebookOption, index: number, arr: PhonebookOption[]) =>
      index === arr.findIndex((candidate: PhonebookOption) => candidate?.matchKey === item?.matchKey)
  );

  return {
    options: [...staticPhonebooks, ...filteredPhonebooks],
    dynamicValues: new Set(filteredPhonebooks.map((item: PhonebookOption) => String(item.value))),
  };
};
